<template>
  <div
    class="grid"
    :class="detailOpen ? 'grid-cols-5' : 'grid-cols-1'"
    @keydown="onKeydown"
    tabindex="0"
    ref="listContainer"
  >
    <!-- List -->
    <div :class="detailOpen ? 'col-span-3' : 'col-span-1'">
      <!-- Stale data banner -->
      <div
        v-if="fetchError"
        class="flex items-center gap-2 px-4 py-2 text-xs text-amber-800 bg-amber-50 border-b border-amber-200"
      >
        <FontAwesomeIcon :icon="['fas', 'exclamation-triangle']" />
        <span>Couldn't refresh — showing the last data we had.</span>
        <button class="font-semibold text-amber-900 hover:underline ml-1" @click="retryNow">Retry</button>
      </div>
      <!-- Toolbar -->
      <div class="flex flex-wrap items-center gap-2 px-4 py-3 border-b border-gray-200">
        <!-- Search -->
        <div class="relative">
          <FontAwesomeIcon
            :icon="['fas', 'search']"
            class="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-300 text-xs"
          />
          <input
            v-model="searchQuery"
            class="text-sm border border-gray-300 rounded pl-8 pr-8 py-1.5 w-40"
            placeholder="Search..."
          />
          <button
            v-if="searchQuery"
            class="absolute right-2.5 inset-y-0 flex items-center text-gray-400 hover:text-gray-600"
            @click="searchQuery = ''"
          >
            <FontAwesomeIcon :icon="['fas', 'xmark']" class="text-xs" />
          </button>
        </div>
        <!-- Type chips: All / Feature Requests / Bugs & Support -->
        <div class="flex items-center gap-1" data-testid="type-chips">
          <button
            type="button"
            class="px-2.5 py-1 text-xs rounded transition-colors"
            :class="!typeChips.length ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            @click="clearTypeChips"
          >All</button>
          <button
            v-for="chip in typeChipGroups"
            :key="chip.key"
            type="button"
            class="px-2.5 py-1 text-xs rounded transition-colors"
            :class="isTypeChipActive(chip) ? 'bg-primary-500 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'"
            @click="toggleTypeChip(chip)"
          >{{ chip.label }}</button>
        </div>
        <!-- Quick filters -->
        <div class="flex rounded border border-gray-300 overflow-hidden">
          <button
            class="text-xs px-2 py-1.5 transition-colors"
            :class="!quickFilter ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            @click="setQuickFilter(null)"
          >All</button>
          <button
            class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
            :class="quickFilter === 'my_open' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            @click="setQuickFilter('my_open')"
          >My Open</button>
          <button
            class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
            :class="quickFilter === 'unassigned' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            @click="setQuickFilter('unassigned')"
          >Unassigned</button>
          <button
            class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
            :class="quickFilter === 'overdue' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            @click="setQuickFilter('overdue')"
          >Overdue</button>
        </div>
        <!-- Filter button -->
        <div class="relative" ref="filterButtonContainer">
          <button
            @click.stop="toggleFilterPopover"
            ref="filterButton"
            class="text-xs border rounded px-2 py-1.5 inline-flex items-center gap-1.5"
            :class="activeFilterCount ? 'text-primary-600 border-primary-300 bg-primary-50' : 'text-gray-500 border-gray-300 hover:text-gray-700'"
          >
            <FontAwesomeIcon :icon="['fas', 'filter']" class="text-[10px]" />
            Filter
            <span v-if="activeFilterCount" class="bg-primary-500 text-white rounded-full text-[10px] px-1.5 py-0.5 leading-none">
              {{ activeFilterCount }}
            </span>
          </button>
          <Support_FilterPopover
            :visible="filterPopoverOpen"
            v-model="activeFilters"
            v-model:hide-completed="hideCompleted"
            :requirement-types="requirementTypes"
            :users="users"
            :tenant-filter-options="tenantFilterOptions"
            :all-tags="allTags"
            :source-filter-options="sourceFilterOptions"
            :anchor-rect="filterButtonRect"
            @close="filterPopoverOpen = false"
          />
        </div>
        <!-- View toggle -->
        <div class="flex rounded border border-gray-300 overflow-hidden">
          <button
            class="text-xs px-2 py-1.5 transition-colors"
            :class="viewMode === 'tree' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            @click="viewMode = 'tree'"
          >
            Tree
          </button>
          <button
            class="text-xs px-2 py-1.5 border-l border-gray-300 transition-colors"
            :class="viewMode === 'table' ? 'bg-primary-50 text-primary-600' : 'text-gray-500 hover:text-gray-700'"
            @click="viewMode = 'table'"
          >
            Table
          </button>
        </div>
        <div class="flex-1" />
        <span
          v-if="refreshing"
          class="text-xs text-gray-400 flex items-center gap-1.5"
        >
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="animate-spin" />
          Updating...
        </span>
        <div v-else-if="updatedLabel" class="flex items-center gap-2 text-xs text-gray-400">
          <span>{{ updatedLabel }}</span>
          <button class="hover:text-gray-600" title="Refresh" @click="retryNow">
            <FontAwesomeIcon :icon="['fas', 'rotate-right']" />
          </button>
        </div>
        <!-- CSV Export -->
        <ExportMenu :disabled="!allFlat.length" :exports="[{ type: 'csv', action: exportCsv }]" />
        <!-- Collapse/Expand all -->
        <button
          class="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 border border-gray-300 rounded"
          @click="toggleExpandAll"
        >
          {{ allExpanded ? 'Collapse' : 'Expand' }}
        </button>
        <!-- Bulk mode toggle -->
        <button
          class="text-xs px-2 py-1.5 border rounded"
          :class="bulkMode
            ? 'text-primary-600 border-primary-300 bg-primary-50'
            : 'text-gray-500 border-gray-300 hover:text-gray-700'"
          @click="toggleBulkMode"
        >
          Select
        </button>
        <!-- New button -->
        <button
          class="bg-primary-500 text-white text-xs font-medium px-3 py-1.5 rounded hover:bg-primary-600 transition-colors"
          @click="showCreateModal = true"
        >
          + New
        </button>
      </div>

      <!-- Active filter chips -->
      <div v-if="filterChips.length || hideCompleted" class="flex flex-wrap items-center gap-1.5 px-4 py-2 border-b border-gray-100 bg-gray-50">
        <Tag
          v-for="(chip, ci) in filterChips"
          :key="'c' + ci"
          :label="chipLabel(chip)"
          :color="isContradictory(chip) ? 'red' : 'default'"
          :removable="true"
          @remove="removeChipValue(chip.filterIdx, chip.value)"
        />
        <Tag
          v-if="hideCompleted"
          label="Hiding completed"
          color="default"
          :removable="true"
          @remove="hideCompleted = false"
        />
        <button @click="clearAllFilters" class="text-xs text-gray-400 hover:text-gray-600 ml-1">Clear all</button>
      </div>

      <!-- Bulk actions bar -->
      <div
        v-if="bulkMode && checkedIds.size > 0"
        class="flex items-center px-6 py-2 bg-primary-50 border-b border-primary-200 space-x-3"
      >
        <span class="text-sm font-medium text-primary-700">{{ checkedIds.size }} selected</span>
        <select
          class="text-sm border border-primary-300 rounded px-2 py-1"
          @change="bulkUpdateStatus($event.target.value); $event.target.value = ''"
        >
          <option value="">Set Status...</option>
          <option value="draft">Draft</option>
          <option value="approved">Approved</option>
          <option value="in_progress">In Progress</option>
          <option value="done">Done</option>
        </select>
        <select
          class="text-sm border border-primary-300 rounded px-2 py-1"
          @change="bulkUpdatePriority($event.target.value); $event.target.value = ''"
        >
          <option value="">Set Priority...</option>
          <option value="p0">P0</option>
          <option value="p1">P1</option>
          <option value="p2">P2</option>
          <option value="p3">P3</option>
        </select>
        <div class="relative">
          <button
            class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
            @click="showBulkReparent = !showBulkReparent"
          >
            Move under...
          </button>
          <div
            v-if="showBulkReparent"
            class="absolute top-full left-0 mt-1 z-50 w-72 bg-white border border-gray-200 rounded-lg shadow-lg"
            @click.stop
          >
            <Support_SearchableSelect
              :model-value="null"
              :options="bulkReparentOptions"
              placeholder="Select new parent..."
              non-placeholder="None (top-level)"
              @update:model-value="bulkReparent($event); showBulkReparent = false"
            />
          </div>
        </div>
        <!-- Bulk add tag -->
        <div class="relative">
          <button
            class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
            @click.stop="bulkDropdown = bulkDropdown === 'add_tag' ? null : 'add_tag'; bulkTagInput = ''"
          >
            Add Tag...
          </button>
          <div
            v-if="bulkDropdown === 'add_tag'"
            class="absolute top-full left-0 mt-1 z-50 w-56 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
            @click.stop
          >
            <input
              v-model="bulkTagInput"
              class="text-xs border border-gray-300 rounded px-2 py-1.5 w-full"
              placeholder="Type tag and press Enter..."
              @keydown.enter.prevent="bulkAddTag(bulkTagInput); bulkDropdown = null"
              @keydown.esc="bulkDropdown = null"
            />
          </div>
        </div>
        <!-- Bulk remove tag -->
        <div class="relative">
          <button
            class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
            @click.stop="bulkDropdown = bulkDropdown === 'remove_tag' ? null : 'remove_tag'"
          >
            Remove Tag...
          </button>
          <div
            v-if="bulkDropdown === 'remove_tag'"
            class="absolute top-full left-0 mt-1 z-50 w-48 bg-white border border-gray-200 rounded-lg shadow-lg py-1"
            @click.stop
          >
            <p v-if="!bulkSelectedTags.length" class="text-xs text-gray-400 px-3 py-2">No tags on selected items</p>
            <button
              v-for="tag in bulkSelectedTags"
              :key="tag"
              class="w-full text-left text-xs px-3 py-1.5 hover:bg-gray-50 text-gray-700"
              @click="bulkRemoveTag(tag); bulkDropdown = null"
            >{{ tag }}</button>
          </div>
        </div>
        <!-- Bulk due date -->
        <div class="relative">
          <button
            class="text-sm border border-primary-300 rounded px-2 py-1 text-primary-700 hover:bg-primary-100"
            @click.stop="bulkDropdown = bulkDropdown === 'due_date' ? null : 'due_date'"
          >
            Due Date...
          </button>
          <div
            v-if="bulkDropdown === 'due_date'"
            class="absolute top-full left-0 mt-1 z-50 bg-white border border-gray-200 rounded shadow-lg p-3"
            @click.stop
          >
            <input
              type="date"
              class="text-xs border border-gray-300 rounded px-2 py-1.5 w-full"
              @change="bulkSetDueDate($event.target.value); bulkDropdown = null"
            />
            <button
              class="w-full text-left text-xs text-gray-400 hover:text-gray-600 mt-2"
              @click="bulkSetDueDate(null); bulkDropdown = null"
            >
              Clear due date
            </button>
          </div>
        </div>
        <button
          class="text-xs text-gray-500 hover:text-gray-700"
          @click="checkedIds = new Set()"
        >
          Clear
        </button>
      </div>

      <!-- List -->
      <div class="overflow-y-auto" style="max-height: calc(100vh - 160px)">
        <div v-if="loading" class="px-6 py-12 text-center">
          <FontAwesomeIcon :icon="['fas', 'spinner']" class="text-gray-300 text-lg animate-spin" />
        </div>
        <template v-else-if="viewMode === 'tree'">
          <div v-if="!filteredTree.length" class="px-6 py-12 text-center text-sm text-gray-400">
            {{ searchQuery ? 'No items match your search.' : 'No items found.' }}
          </div>
          <draggable
            :list="filteredTree"
            item-key="id"
            :group="{ name: 'requirements' }"
            handle=".drag-handle"
            ghost-class="drag-ghost"
            chosen-class="drag-chosen"
            data-parent-id=""
            @start="onRootDragStart"
            @end="onRootDragEnd"
          >
            <template #item="{ element }">
              <Support_Row
                :node="element"
                :depth="0"
                :selected-id="selectedRequirement?.id"
                :expanded-ids="expandedIds"
                :bulk-mode="bulkMode"
                :checked-ids="checkedIds"
                :keyboard-focused-id="keyboardFocusedId"
                :tenant-options="tenantOptions"
                :users="users"
                :comment-counts="commentCounts"
                :filter-match-ids="filterMatchIds"
                :has-active-filters="hasActiveFilters"
                @select="onSelect"
                @toggle="onToggle"
                @quick-add="onQuickAdd"
                @toggle-check="onToggleCheck"
                @update-field="onUpdateField"
                @update-title="onUpdateTitle"
                @reorder="onReorder"
                @reparent="onReparent"
                @actions-menu="onActionsMenu"
              />
            </template>
          </draggable>
          <!-- Quick-add input -->
          <Support_QuickAdd
            v-if="quickAddParent"
            :parent-node="quickAddParent"
            :depth="quickAddDepth"
            :requirement-types="requirementTypes"
            @created="onQuickAddCreated"
            @cancel="quickAddParent = null"
          />
        </template>
        <template v-else>
          <div v-if="!tableData.length" class="px-6 py-12 text-center text-sm text-gray-400">
            {{ searchQuery ? 'No items match your search.' : 'No items found.' }}
          </div>
          <Support_Table
            v-else
            :items="tableData"
            :all-items="allFlat"
            :selected-id="selectedRequirement?.id"
            :comment-counts="commentCounts"
            :filter-match-ids="filterMatchIds"
            :has-active-filters="hasActiveFilters"
            :sort-field="sortField"
            :sort-dir="sortDir"
            @select="onSelect"
            @sort="onSortColumn"
          />
        </template>
      </div>
    </div>

    <!-- Detail Panel -->
    <div v-if="detailOpen" class="col-span-2 border-l border-gray-200 overflow-y-auto" style="max-height: calc(100vh - 160px)">
      <Support_Detail
        :requirement="selectedRequirement"
        :requirement-types="requirementTypes"
        :users="users"
        :tenant-options="tenantOptions"
        :all-items="allFlat"
        @close="closeDetail"
        @updated="onRequirementUpdated"
        @deleted="onRequirementDeleted"
        @comment-count="onCommentCount"
        @select-item="onSelectItem"
      />
    </div>

    <!-- Create Modal -->
    <Support_CreateModal
      v-if="showCreateModal"
      :requirement-types="requirementTypes"
      :users="users"
      :tenant-options="tenantOptions"
      :parent-options="allFlat"
      :default-parent="createModalParentId"
      @close="showCreateModal = false; createModalParentId = null"
      @created="onRequirementCreated"
    />

    <!-- Context Menu -->
    <Support_ContextMenu
      :visible="contextMenu.visible"
      :x="contextMenu.x"
      :y="contextMenu.y"
      :item="contextMenu.item"
      @close="contextMenu.visible = false"
      @add-child="openCreateWithParent"
      @change-parent="onContextChangeParent"
      @copy-link="onContextCopyLink"
      @delete="onContextDelete"
      @delete-promote="onContextDeletePromote"
    />
    <Support_UndoToast ref="undoToast" />
  </div>
</template>

<script>
import axios from 'axios';
import debounce from 'lodash/debounce';
import { z } from 'zod';
import { mapGetters } from 'vuex';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import draggable from 'vuedraggable';
import Support_Detail from './Support_Detail.vue';
import Support_CreateModal from './Support_CreateModal.vue';
import Support_Row from './Support_Row.vue';
import Support_QuickAdd from './Support_QuickAdd.vue';
import Support_Table from './Support_Table.vue';
import Support_FilterPopover from './Support_FilterPopover.vue';
import Support_ContextMenu from './Support_ContextMenu.vue';
import Support_SearchableSelect from './Support_SearchableSelect.vue';
import Support_UndoToast from './Support_UndoToast.vue';
import {
  calendarDayValue,
  canParent,
  compareWorkItems,
  daysUntil,
  defaultSortFor,
  evictStaleItemCaches,
  getDescendantIds,
  matchesSearch,
  TYPE_CHIP_GROUPS,
  WORK_TYPES,
} from './support_constants.js';
import { useToast } from 'vue-toastification';
import Tag from '@/components/widgetcomponents/library/Tag.vue';
import ExportMenu from '@/components/widgetcomponents/library/ExportMenu.vue';
import { BrowserStore } from '@/utils/BrowserStore';

// Fields whose filter value is a single scalar (a date string or a number)
// rather than an array of selections. Their operators are on_or_before /
// on_or_after; a range is two rows on the same field.
const DATE_FILTER_FIELDS = new Set(['due_date', 'created_at', 'updated_at']);
const COUNT_FILTER_FIELDS = new Set(['comment_count', 'children_count']);
const SCALAR_OPERATORS = new Set(['on_or_before', 'on_or_after']);

// Drafts are reachable but not shown by default. This is a real filter row the
// user can remove -- not a hard exclusion -- which is what turns Cerberus's 44
// draft Bugs from invisible clutter into an ownable view. Seeded in data() so a
// restored or URL-supplied filter set replaces it wholesale; nothing re-adds it
// after the user clears it.
function defaultActiveFilters() {
  return [{ field: 'status', operator: 'none_of', value: ['draft'] }];
}

// A row's count for a scalar count filter. `comment_count` is only present on
// rows the backend ships it for; commentCounts fills in whatever the detail
// drawer has since reported, and anything still unknown reads as 0.
function countValue(row, field, commentCounts) {
  if (field === 'comment_count') {
    return row.comment_count ?? commentCounts?.[row.id] ?? 0;
  }
  return row[field] ?? 0;
}

function matchesDateBounds(row, bounds) {
  for (const { field, after, before } of bounds) {
    const day = calendarDayValue(row[field]);
    // A row with no date can't satisfy a date bound. It is excluded rather than
    // admitted, so "due on or before Friday" never returns the undated backlog.
    if (day === null) return false;
    if (after !== null && day < after) return false;
    if (before !== null && day > before) return false;
  }
  return true;
}

function matchesCountBounds(row, bounds, commentCounts) {
  for (const { field, min, max } of bounds) {
    const n = countValue(row, field, commentCounts);
    if (Number.isFinite(min) && n < min) return false;
    if (Number.isFinite(max) && n > max) return false;
  }
  return true;
}

function isScalarFilter(filter) {
  return SCALAR_OPERATORS.has(filter?.operator);
}

const SOURCE_LABELS = {
  email: 'Email',
  web: 'Web',
  internal_note: 'Internal Note',
};
// Loose on purpose: requirement shape is owned by the backend and evolves
// independently of this cache. We only need "an array of objects" to keep a
// malformed/truncated response from poisoning allFlat — see BrowserStore.
// Wrapped in an object because BrowserStore.load()'s invalid-data fallback is
// schema.parse({}), which only resolves to defaults for an object-root schema.
const CACHED_REQUIREMENTS_SCHEMA = z.object({
  items: z.array(z.record(z.string(), z.unknown())).default([]),
});

// One merged list, one cache prefix. The retired per-tab prefixes
// (support_list_requirements / support_list_tickets) are swept by
// evictStaleItemCaches -- see DEAD_CACHE_PREFIXES in support_constants.js.
const CACHE_PREFIX = 'support_list';
const FILTER_STATE_KEY = 'support_list_filters';
const VIEW_MODES = ['table', 'tree'];

function cacheKeyFor(tenantSlug, userId) {
  if (!tenantSlug || userId == null) return null;
  return `${CACHE_PREFIX}:${tenantSlug}:${userId}`;
}

export default {
  name: 'Support_List',
  components: {
    FontAwesomeIcon,
    draggable,
    Support_Detail,
    Support_CreateModal,
    Support_Row,
    Support_QuickAdd,
    Support_Table,
    Support_FilterPopover,
    Support_ContextMenu,
    Support_SearchableSelect,
    Support_UndoToast,
    Tag,
    ExportMenu,
  },
  props: {
    selectedId: { type: [String, Number], default: null },
    entityId: { type: [String, Number], default: null },
  },
  emits: ['select'],
  data() {
    return {
      // Kevin wants a flat list of things to close by due date, not an Epic
      // tree. Tree stays one click away for Irene.
      viewMode: 'table',
      loading: true,
      refreshing: false,
      allFlat: [],
      requirementTypes: [],
      users: [],
      selectedRequirement: null,
      searchQuery: '',
      // Sort is per view mode, and both are remembered: switching to the tree
      // to drag something and back must not clobber a deliberate table sort.
      sortByMode: {
        table: { ...defaultSortFor('table') },
        tree: { ...defaultSortFor('tree') },
      },
      showCreateModal: false,
      createModalParentId: null,
      contextMenu: { visible: false, x: 0, y: 0, item: null },
      showBulkReparent: false,
      bulkDropdown: null,
      bulkTagInput: '',
      expandedIds: new Set(),
      bulkMode: false,
      checkedIds: new Set(),
      keyboardFocusedId: null,
      quickAddParent: null,
      quickFilter: null,
      // Type names narrowed by the chip group; empty means All.
      typeChips: [],
      activeFilters: defaultActiveFilters(),
      hideCompleted: true,
      filterPopoverOpen: false,
      filterButtonRect: null,
      tenantDefaultApplied: false,
      commentCounts: {},
      lastFetchedAt: null,
      fetchError: false,
      retryAttempt: 0,
      now: Date.now(),
    };
  },
  computed: {
    ...mapGetters('auth', ['current_user']),
    ...mapGetters('tenant', ['current_tenant_slug']),
    toast() {
      return useToast();
    },
    detailOpen() {
      return this.selectedRequirement !== null;
    },
    updatedLabel() {
      if (!this.lastFetchedAt) return '';
      const diffSec = Math.max(0, Math.round((this.now - this.lastFetchedAt) / 1000));
      if (diffSec < 10) return 'Updated just now';
      if (diffSec < 60) return `Updated ${diffSec}s ago`;
      const diffMin = Math.round(diffSec / 60);
      if (diffMin < 60) return `Updated ${diffMin}m ago`;
      const diffHr = Math.round(diffMin / 60);
      return `Updated ${diffHr}h ago`;
    },
    tenantOptions() {
      const seen = new Map();
      for (const r of this.allFlat) {
        if (r.tenant && !seen.has(r.tenant)) {
          seen.set(r.tenant, { id: r.tenant, name: r.tenant_name });
        }
      }
      return [...seen.values()].sort((a, b) => a.name.localeCompare(b.name));
    },
    tenantFilterOptions() {
      const options = this.tenantOptions.map(t => ({ value: t.id, label: t.name }));
      const hasNull = this.allFlat.some(r => !r.tenant);
      if (hasNull) {
        options.unshift({ value: '__none__', label: 'No Tenant' });
      }
      return options;
    },
    sourceFilterOptions() {
      const seen = new Set(this.allFlat.map(r => r.source || '__none__'));
      const options = Object.keys(SOURCE_LABELS)
        .filter(key => seen.has(key))
        .map(key => ({ value: key, label: SOURCE_LABELS[key] }));
      if (seen.has('__none__')) options.unshift({ value: '__none__', label: 'No source' });
      return options;
    },
    typeChipGroups() {
      return TYPE_CHIP_GROUPS;
    },
    // Sorting the Parent column has to compare titles, not the raw parent ids.
    parentTitles() {
      const map = {};
      for (const item of this.allFlat) map[item.id] = item.title;
      return map;
    },
    // Type names the chips admit. Empty chip selection means every work type,
    // which is the "All" view neither old tab offered.
    allowedTypeNames() {
      return this.typeChips.length ? new Set(this.typeChips) : WORK_TYPES;
    },
    // Ids of the users loadUsers() considers Kairon staff, for owner_is_staff.
    staffUserIds() {
      return new Set(this.users.map(u => u.id));
    },
    filterChips() {
      const chips = [];
      for (const [idx, filter] of this.activeFilters.entries()) {
        if (isScalarFilter(filter)) {
          chips.push({ filterIdx: idx, field: filter.field, operator: filter.operator, value: filter.value, scalar: true });
          continue;
        }
        if (!Array.isArray(filter.value) || !filter.value.length) {
          chips.push({ filterIdx: idx, field: filter.field, operator: filter.operator, value: null });
          continue;
        }
        for (const val of filter.value) {
          chips.push({ filterIdx: idx, field: filter.field, operator: filter.operator, value: val });
        }
      }
      return chips;
    },
    selectedTenantIds() {
      const f = this.activeFilters.find(f => f.field === 'tenant' && f.operator === 'any_of');
      return f?.value?.length ? f.value : null;
    },
    selectedTags() {
      const f = this.activeFilters.find(f => f.field === 'tags' && f.operator === 'any_of');
      return f?.value?.length ? f.value : [];
    },
    activeFilterCount() {
      const valCount = this.activeFilters.reduce(
        (n, f) => n + (isScalarFilter(f) ? 1 : f.value?.length || 0),
        0,
      );
      return valCount + (this.hideCompleted ? 1 : 0);
    },
    // The active mode's sort, read and written as if it were still a pair of
    // plain fields -- every existing call site (URL sync, column header,
    // sortItems) keeps working and lands in the right mode's slot.
    // Falls back the way defaultSortFor() does rather than indexing blind:
    // viewMode is restored from localStorage, so an unrecognized value would
    // otherwise be a render-time TypeError instead of a harmless default.
    activeSortSlot() {
      return this.sortByMode[this.viewMode] || this.sortByMode.table;
    },
    sortField: {
      get() { return this.activeSortSlot.field; },
      set(field) { this.activeSortSlot.field = field; },
    },
    sortDir: {
      get() { return this.activeSortSlot.dir; },
      set(dir) { this.activeSortSlot.dir = dir; },
    },
    activeDefaultSort() {
      return defaultSortFor(this.viewMode);
    },
    listFilterState() {
      return {
        searchQuery: this.searchQuery,
        sortByMode: this.sortByMode,
        viewMode: this.viewMode,
        quickFilter: this.quickFilter,
        typeChips: this.typeChips,
        activeFilters: this.activeFilters,
        hideCompleted: this.hideCompleted,
      };
    },
    filterParams() {
      const params = {};
      const getOnlyVal = (field) => {
        const f = this.activeFilters.find(f => f.field === field && f.operator === 'any_of');
        return f?.value?.length === 1 ? f.value[0] : undefined;
      };
      const t = getOnlyVal('type'); if (t !== undefined) params.requirement_type = t;
      const s = getOnlyVal('status'); if (s !== undefined) params.status = s;
      const o = getOnlyVal('owner'); if (o !== undefined && o !== '__none__') params.owner = o;
      return params;
    },
    _tabFilterResult() {
      // The chips narrow this; with no chip active it is every work type. The
      // ancestor walk below reads the same set, which is what keeps a Bug
      // parented to an Epic from dragging the Epic in while a chip excludes
      // Epics (#2237).
      const allowed = this.allowedTypeNames;
      const done = new Set(['done', 'resolved', 'closed']);
      const getVals = (field, op) => {
        const f = this.activeFilters.find(f => f.field === field && f.operator === op);
        return f?.value?.length ? f.value : null;
      };
      // Scalar filters hold a single value, never an array -- reading them
      // through getVals() would silently drop them (a bare string has .length).
      const getScalar = (field, op) => {
        const f = this.activeFilters.find(f => f.field === field && f.operator === op);
        if (!f || Array.isArray(f.value) || f.value === '' || f.value == null) return null;
        return f.value;
      };
      // Both sides flatten to a local calendar day, so a bare due_date and a
      // full created_at timestamp compare the same way and a date-only value is
      // never parsed as UTC midnight.
      const dateBounds = [...DATE_FILTER_FIELDS].map((field) => ({
        field,
        after: calendarDayValue(getScalar(field, 'on_or_after')),
        before: calendarDayValue(getScalar(field, 'on_or_before')),
      })).filter(b => b.after !== null || b.before !== null);
      const countBounds = [...COUNT_FILTER_FIELDS].map((field) => {
        const min = getScalar(field, 'on_or_after');
        const max = getScalar(field, 'on_or_before');
        return {
          field,
          min: min == null ? null : Number(min),
          max: max == null ? null : Number(max),
        };
      }).filter(b => Number.isFinite(b.min) || Number.isFinite(b.max));
      // Only take the reactive dependency when a comment bound is actually set:
      // otherwise every count reported by the detail drawer would invalidate
      // the whole list.
      const commentCounts = countBounds.some(b => b.field === 'comment_count')
        ? this.commentCounts
        : null;
      const typeAny = getVals('type', 'any_of');
      const typeNone = getVals('type', 'none_of');
      const statusAny = getVals('status', 'any_of');
      const statusNone = getVals('status', 'none_of');
      const ownerAny = getVals('owner', 'any_of');
      const ownerNone = getVals('owner', 'none_of');
      const requesterAny = getVals('requester', 'any_of');
      const requesterNone = getVals('requester', 'none_of');
      const priorityAny = getVals('priority', 'any_of');
      const priorityNone = getVals('priority', 'none_of');
      const tenantNone = getVals('tenant', 'none_of');
      const tagsNone = getVals('tags', 'none_of');
      const cfAny = getVals('customer_facing', 'any_of');
      const cfNone = getVals('customer_facing', 'none_of');
      const sourceAny = getVals('source', 'any_of');
      const sourceNone = getVals('source', 'none_of');
      const triageAny = getVals('needs_triage', 'any_of');
      const triageNone = getVals('needs_triage', 'none_of');
      const staffAny = getVals('owner_is_staff', 'any_of');
      const staffNone = getVals('owner_is_staff', 'none_of');
      const staffIds = this.staffUserIds;
      const quickDone = new Set(['done', 'resolved', 'closed']);
      const currentUserId = this.current_user?.id;
      const ancestorsWanted = this.viewMode === 'tree';
      const trueMatchIds = new Set();
      const includeIds = new Set();
      const allById = Object.fromEntries(this.allFlat.map(r => [r.id, r]));
      for (const r of this.allFlat) {
        if (!allowed.has(r.requirement_type_name)) continue;
        if (this.quickFilter === 'my_open' && (quickDone.has(r.status) || r.owner !== currentUserId)) continue;
        if (this.quickFilter === 'unassigned' && r.owner) continue;
        // daysUntil() normalizes both sides to local midnight, so a date-only
        // due_date no longer reads as overdue a day early west of UTC. A
        // missing or unparseable date returns null and is never overdue.
        if (this.quickFilter === 'overdue' && !(daysUntil(r.due_date) < 0)) continue;
        if (this.hideCompleted && done.has(r.status)) continue;
        if (typeAny && !typeAny.includes(r.requirement_type)) continue;
        if (typeNone && typeNone.includes(r.requirement_type)) continue;
        if (statusAny && !statusAny.includes(r.status)) continue;
        if (statusNone && statusNone.includes(r.status)) continue;
        if (ownerAny) {
          if (!r.owner && !ownerAny.includes('__none__')) continue;
          if (r.owner && !ownerAny.includes(r.owner)) continue;
        }
        if (ownerNone) {
          if (!r.owner && ownerNone.includes('__none__')) continue;
          if (r.owner && ownerNone.includes(r.owner)) continue;
        }
        if (requesterAny) {
          if (!r.requester && !requesterAny.includes('__none__')) continue;
          if (r.requester && !requesterAny.includes(r.requester)) continue;
        }
        if (requesterNone) {
          if (!r.requester && requesterNone.includes('__none__')) continue;
          if (r.requester && requesterNone.includes(r.requester)) continue;
        }
        if (priorityAny && !priorityAny.includes(r.priority)) continue;
        if (priorityNone && priorityNone.includes(r.priority)) continue;
        if (!this.entityId && this.selectedTenantIds) {
          if (!r.tenant && !this.selectedTenantIds.includes('__none__')) continue;
          if (r.tenant && !this.selectedTenantIds.includes(r.tenant)) continue;
        }
        if (tenantNone) {
          if (!r.tenant && tenantNone.includes('__none__')) continue;
          if (r.tenant && tenantNone.includes(r.tenant)) continue;
        }
        if (tagsNone && r.tags?.some(t => tagsNone.includes(t))) continue;
        if (cfAny) {
          const val = r.customer_facing ? 'yes' : 'no';
          if (!cfAny.includes(val)) continue;
        }
        if (cfNone) {
          const val = r.customer_facing ? 'yes' : 'no';
          if (cfNone.includes(val)) continue;
        }
        if (sourceAny && !sourceAny.includes(r.source || '__none__')) continue;
        if (sourceNone && sourceNone.includes(r.source || '__none__')) continue;
        if (triageAny && !triageAny.includes(r.needs_triage ? 'yes' : 'no')) continue;
        if (triageNone && triageNone.includes(r.needs_triage ? 'yes' : 'no')) continue;
        if (staffAny || staffNone) {
          // Unowned counts as "not staff-owned": there is no staff member on
          // the row to be staff.
          const val = r.owner && staffIds.has(r.owner) ? 'yes' : 'no';
          if (staffAny && !staffAny.includes(val)) continue;
          if (staffNone && staffNone.includes(val)) continue;
        }
        if (!matchesDateBounds(r, dateBounds)) continue;
        if (!matchesCountBounds(r, countBounds, commentCounts)) continue;
        trueMatchIds.add(r.id);
        includeIds.add(r.id);
        // Context rows are a TREE concern only. A tree needs the ancestor to
        // draw a path through; a flat sorted table does not -- there the row is
        // a standalone entry that either matched or didn't, and its Parent
        // column already carries the ancestor's title (parentMap reads allFlat,
        // so the label survives whether or not the row is in `items`).
        //
        // Including them in the table is what made sorting look broken: the
        // table blanks Status/Priority/Due Date on a context row, but the
        // comparator still ranks it by its real hidden values, so a P2 Epic
        // pulled in by a matching child rendered as an empty row sitting
        // between the P1s and the P2s. Same for due date.
        if (!ancestorsWanted) continue;
        // Stop at the first ancestor this chip selection isn't allowed to
        // display: you can't render a tree through a row you're not permitted
        // to show, so an out-of-scope ancestor severs the chain. buildTree()
        // re-roots anything whose parent didn't make the cut.
        let cur = r;
        while (cur.parent && allById[cur.parent]) {
          const parent = allById[cur.parent];
          if (!allowed.has(parent.requirement_type_name)) break;
          includeIds.add(parent.id);
          cur = parent;
        }
      }
      return { items: this.allFlat.filter(r => includeIds.has(r.id)), trueMatchIds };
    },
    tabFiltered() {
      return this._tabFilterResult.items;
    },
    filterMatchIds() {
      return this._tabFilterResult.trueMatchIds;
    },
    hasActiveFilters() {
      return this.activeFilters.length > 0 || this.quickFilter || this.hideCompleted || this.typeChips.length > 0 || this.selectedTags.length > 0 || this.searchQuery.trim();
    },
    allTags() {
      return [...new Set(this.allFlat.flatMap(r => r.tags || []))].sort();
    },
    bulkSelectedTags() {
      const tagSet = new Set();
      for (const id of this.checkedIds) {
        const item = this.allFlat.find(r => r.id === id);
        for (const tag of item?.tags || []) tagSet.add(tag);
      }
      return [...tagSet].sort();
    },
    tagFiltered() {
      if (!this.selectedTags.length) return this.tabFiltered;
      const byId = Object.fromEntries(this.tabFiltered.map(r => [r.id, r]));
      const keepIds = new Set();
      // Same rule as the type-scoped walk above: ancestors are scaffolding for
      // the tree, never rows in their own right in the table.
      const ancestorsWanted = this.viewMode === 'tree';
      for (const r of this.tabFiltered) {
        if (r.tags?.some(t => this.selectedTags.includes(t))) {
          keepIds.add(r.id);
          if (!ancestorsWanted) continue;
          let cur = r;
          while (cur.parent && byId[cur.parent]) {
            keepIds.add(cur.parent);
            cur = byId[cur.parent];
          }
        }
      }
      return this.tabFiltered.filter(r => keepIds.has(r.id));
    },
    tableData() {
      if (!this.searchQuery.trim()) return this.sortItems([...this.tagFiltered]);
      const q = this.searchQuery.trim();
      const ranked = [];
      for (const r of this.tagFiltered) {
        const rank = matchesSearch(r, q);
        if (rank != null) ranked.push({ item: r, rank });
      }
      ranked.sort((a, b) => a.rank - b.rank);
      return this.sortItems(ranked.map((r) => r.item));
    },
    tree() {
      return this.buildTree(this.tagFiltered);
    },
    filteredTree() {
      if (!this.searchQuery.trim()) return this.tree;
      const q = this.searchQuery.toLowerCase();
      return this.filterTree(this.tree, q);
    },
    allExpanded() {
      const expandable = this.allFlat.filter(r => r.children_count > 0);
      return expandable.length > 0 && expandable.every(r => this.expandedIds.has(r.id));
    },
    flatVisibleIds() {
      // Flattened list of visible node IDs for keyboard navigation
      const ids = [];
      const walk = (nodes) => {
        for (const node of nodes) {
          ids.push(node.id);
          if (this.expandedIds.has(node.id) && node.children?.length) {
            walk(node.children);
          }
        }
      };
      walk(this.filteredTree);
      return ids;
    },
    bulkReparentOptions() {
      if (!this.checkedIds.size) return this.allFlat;
      const excluded = getDescendantIds([...this.checkedIds], this.allFlat);
      // Every checked row has to be able to live under the candidate. Without
      // this the merged list offers a Bug a Requirement parent -- one click
      // from a hierarchy the create modal and drawer both refuse to build.
      const selectedTypes = [...new Set(
        [...this.checkedIds]
          .map((id) => this.allFlat.find((r) => r.id === id)?.requirement_type_name)
          .filter(Boolean),
      )];
      return this.allFlat.filter((item) => {
        if (excluded.has(item.id)) return false;
        return selectedTypes.every((childType) => canParent(item.requirement_type_name, childType));
      });
    },
    quickAddDepth() {
      if (!this.quickAddParent) return 0;
      let depth = 1;
      let current = this.quickAddParent;
      const byId = {};
      for (const item of this.allFlat) byId[item.id] = item;
      while (current.parent && byId[current.parent]) {
        depth++;
        current = byId[current.parent];
      }
      return depth;
    },
  },
  watch: {
    detailOpen() {
      // Layout shift invalidates popover position
      this.filterPopoverOpen = false;
    },
    activeFilters: {
      deep: true,
      handler(newFilters, oldFilters) {
        if (this.loading) return;
        if (this._syncGuard === 0) {
          const serverFields = ['type', 'status'];
          const getServer = (filters) => filters.filter(f => serverFields.includes(f.field) && f.operator === 'any_of');
          if (JSON.stringify(getServer(newFilters)) !== JSON.stringify(getServer(oldFilters))) {
            this.loadRequirements();
          }
        }
        this.syncFiltersToUrl();
      },
    },
    searchQuery(q) {
      if (this._syncGuard === 0 && q.trim()) {
        const query = q.toLowerCase();
        const expand = new Set();
        const walk = (nodes, ancestors) => {
          for (const node of nodes) {
            if (node.title.toLowerCase().includes(query)) {
              for (const id of ancestors) expand.add(id);
            }
            if (node.children?.length) {
              walk(node.children, [...ancestors, node.id]);
            }
          }
        };
        walk(this.tree, []);
        if (expand.size) {
          const merged = new Set(this.expandedIds);
          for (const id of expand) merged.add(id);
          this.expandedIds = merged;
        }
      }
      this._debouncedSyncUrl();
    },
    filterPopoverOpen(open) {
      if (open) {
        this.updateFilterButtonRect();
        window.addEventListener('scroll', this.updateFilterButtonRect, true);
        window.addEventListener('resize', this.updateFilterButtonRect);
      } else {
        window.removeEventListener('scroll', this.updateFilterButtonRect, true);
        window.removeEventListener('resize', this.updateFilterButtonRect);
      }
    },
    hideCompleted() {
      this.syncFiltersToUrl();
    },
    typeChips() {
      this.syncFiltersToUrl();
    },
    sortField() {
      this.syncFiltersToUrl();
    },
    sortDir() {
      this.syncFiltersToUrl();
    },
    '$route.query': {
      handler(newQuery, oldQuery) {
        if (this._syncGuard > 0) return;
        if (JSON.stringify(newQuery) === JSON.stringify(oldQuery)) return;
        this.parseQueryToFilters();
      },
    },
    selectedId(newId) {
      if (newId && this.allFlat.length) {
        this.selectedRequirement = this.allFlat.find(r => r.id === Number(newId)) || null;
      } else if (!newId) {
        this.selectedRequirement = null;
      }
    },
    current_tenant_slug() {
      this.reloadForCacheContext();
    },
    'current_user.id'() {
      this.reloadForCacheContext();
    },
    listFilterState: {
      deep: true,
      handler(state) {
        if (this.loading) return;
        localStorage.setItem(FILTER_STATE_KEY, JSON.stringify(state));
      },
    },
  },
  created() {
    this._syncGuard = 0;
    this._urlSyncPending = false;
    this._debouncedSyncUrl = debounce(() => this.syncFiltersToUrl(), 300);
  },
  async mounted() {
    this._syncGuard++;
    const filterFields = ['owner', 'tenant', 'status', 'type', 'priority', 'tags', 'customer_facing', 'requester', 'source', 'needs_triage', 'owner_is_staff'];
    const scalarKeys = [...DATE_FILTER_FIELDS, ...COUNT_FILTER_FIELDS].flatMap(f => [`${f}_from`, `${f}_to`]);
    const filterKeys = new Set([
      ...filterFields,
      ...filterFields.map(f => `${f}_not`),
      ...scalarKeys,
      'search', 'hide_done', 'sort', 'dir', 'types',
    ]);
    if (Object.keys(this.$route.query).some(k => filterKeys.has(k))) {
      this.parseQueryToFilters();
    } else {
      this.restoreFilters();
    }
    this.$nextTick(() => { this._syncGuard--; });
    this.loadRequirementTypes();
    this.loadUsers();
    await this.loadRequirements();
    window.addEventListener('focus', this.onRevalidate);
    document.addEventListener('visibilitychange', this.onRevalidate);
    this._closeBulkDropdown = () => { this.bulkDropdown = null; };
    document.addEventListener('click', this._closeBulkDropdown);
    this.pollTimer = setInterval(this.onRevalidate, 60000);
    this.clockTimer = setInterval(() => { this.now = Date.now(); }, 15000);
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.updateFilterButtonRect, true);
    window.removeEventListener('resize', this.updateFilterButtonRect);
    window.removeEventListener('focus', this.onRevalidate);
    document.removeEventListener('click', this._closeBulkDropdown);
    document.removeEventListener('visibilitychange', this.onRevalidate);
    clearInterval(this.pollTimer);
    clearInterval(this.clockTimer);
    clearTimeout(this.retryTimer);
    this._debouncedSyncUrl?.cancel();
  },
  methods: {
    onRevalidate() {
      if (document.visibilityState === 'visible') this.loadRequirements();
    },
    restoreFilters() {
      try {
        const saved = localStorage.getItem(FILTER_STATE_KEY);
        if (!saved) return;
        const s = JSON.parse(saved);
        if (s.searchQuery) this.searchQuery = s.searchQuery;
        // viewMode first: sortField/sortDir are views onto the active mode's
        // slot, so restoring them before the mode would write to the wrong one.
        if (s.viewMode && VIEW_MODES.includes(s.viewMode)) this.viewMode = s.viewMode;
        for (const mode of ['table', 'tree']) {
          const saved = s.sortByMode?.[mode];
          if (saved?.field) this.sortByMode[mode].field = saved.field;
          if (saved?.dir) this.sortByMode[mode].dir = saved.dir;
        }
        if (s.quickFilter !== undefined) this.quickFilter = s.quickFilter;
        if (Array.isArray(s.typeChips)) this.typeChips = s.typeChips;
        // Restored wholesale, including an empty array: a user who cleared the
        // default draft filter must not have it seeded back on the next visit.
        if (Array.isArray(s.activeFilters)) this.activeFilters = s.activeFilters;
        if (s.hideCompleted != null) this.hideCompleted = s.hideCompleted;
      } catch {
        // ignore
      }
    },
    updateFilterButtonRect() {
      const btn = this.$refs.filterButton;
      if (btn) this.filterButtonRect = btn.getBoundingClientRect();
    },
    syncFiltersToUrl() {
      if (this._syncGuard > 0) {
        this._urlSyncPending = true;
        return;
      }
      this._urlSyncPending = false;
      const query = {};
      for (const f of this.activeFilters) {
        if (isScalarFilter(f)) {
          if (f.value === '' || f.value == null) continue;
          query[`${f.field}${f.operator === 'on_or_before' ? '_to' : '_from'}`] = String(f.value);
          continue;
        }
        if (!f.value?.length) continue;
        const key = f.operator === 'none_of' ? `${f.field}_not` : f.field;
        query[key] = f.value.join(',');
      }
      if (this.typeChips.length) query.types = this.typeChips.join(',');
      if (this.hideCompleted) query.hide_done = '1';
      if (this.sortField !== this.activeDefaultSort.field) query.sort = this.sortField;
      if (this.sortDir !== this.activeDefaultSort.dir) query.dir = this.sortDir;
      if (this.searchQuery.trim()) query.search = this.searchQuery.trim();

      const current = this.$route.query;
      const keys = new Set([...Object.keys(query), ...Object.keys(current)]);
      let changed = false;
      for (const k of keys) {
        if (String(query[k] || '') !== String(current[k] || '')) {
          changed = true;
          break;
        }
      }
      if (!changed) return;

      this._syncGuard++;
      this.$router.replace({ query })
        .catch(() => {})
        .finally(() => {
          this._syncGuard--;
          if (this._urlSyncPending) this.syncFiltersToUrl();
        });
    },
    parseQueryToFilters() {
      const q = this.$route.query;
      this._syncGuard++;

      const serverFields = ['type', 'status', 'owner'];
      const getServer = (filters) => filters.filter(f => serverFields.includes(f.field) && f.operator === 'any_of');
      const prevServer = JSON.stringify(getServer(this.activeFilters));

      const toStr = (v) => Array.isArray(v) ? v.join(',') : v;
      const filters = [];
      const numericFields = ['owner', 'requester', 'type'];
      const stringFields = ['status', 'priority', 'tags', 'customer_facing', 'source', 'needs_triage', 'owner_is_staff'];

      const parseNum = v => {
        const n = Number(v);
        return Number.isNaN(n) ? null : n;
      };
      const parseStr = v => v || null;
      const parseTenant = v => v === '__none__' ? v : parseNum(v);

      const addFilter = (field, operator, raw, parseVal) => {
        const values = toStr(raw).split(',').map(parseVal).filter(v => v !== null);
        if (values.length) filters.push({ field, operator, value: values });
      };

      for (const field of numericFields) {
        if (q[field]) addFilter(field, 'any_of', q[field], parseNum);
        if (q[`${field}_not`]) addFilter(field, 'none_of', q[`${field}_not`], parseNum);
      }
      for (const field of stringFields) {
        if (q[field]) addFilter(field, 'any_of', q[field], parseStr);
        if (q[`${field}_not`]) addFilter(field, 'none_of', q[`${field}_not`], parseStr);
      }
      if (q.tenant) addFilter('tenant', 'any_of', q.tenant, parseTenant);
      if (q.tenant_not) addFilter('tenant', 'none_of', q.tenant_not, parseTenant);

      for (const field of DATE_FILTER_FIELDS) {
        if (q[`${field}_from`]) filters.push({ field, operator: 'on_or_after', value: toStr(q[`${field}_from`]) });
        if (q[`${field}_to`]) filters.push({ field, operator: 'on_or_before', value: toStr(q[`${field}_to`]) });
      }
      for (const field of COUNT_FILTER_FIELDS) {
        const from = q[`${field}_from`] ? parseNum(toStr(q[`${field}_from`])) : null;
        const to = q[`${field}_to`] ? parseNum(toStr(q[`${field}_to`])) : null;
        if (from !== null) filters.push({ field, operator: 'on_or_after', value: from });
        if (to !== null) filters.push({ field, operator: 'on_or_before', value: to });
      }

      this.typeChips = q.types ? toStr(q.types).split(',').filter(Boolean) : [];
      this.activeFilters = filters;
      this.hideCompleted = q.hide_done === '1';
      this.sortField = toStr(q.sort) || this.activeDefaultSort.field;
      this.sortDir = toStr(q.dir) === 'desc' ? 'desc' : this.activeDefaultSort.dir;
      this.searchQuery = toStr(q.search) || '';
      this.tenantDefaultApplied = filters.some(f => f.field === 'tenant');

      this.$nextTick(() => { this._syncGuard--; });

      if (!this.loading && JSON.stringify(getServer(filters)) !== prevServer) {
        this.loadRequirements();
      }
    },
    toggleFilterPopover() {
      if (!this.filterPopoverOpen) {
        this.updateFilterButtonRect();
      }
      this.filterPopoverOpen = !this.filterPopoverOpen;
    },
    removeChipValue(filterIdx, value) {
      if (value === null || isScalarFilter(this.activeFilters[filterIdx])) {
        this.activeFilters = this.activeFilters.filter((_, i) => i !== filterIdx);
        return;
      }
      this.activeFilters = this.activeFilters.map((f, i) => {
        if (i !== filterIdx) return f;
        const newVal = f.value.filter(v => v !== value);
        return newVal.length ? { ...f, value: newVal } : null;
      }).filter(Boolean);
    },
    setQuickFilter(value) {
      this.quickFilter = value;
    },
    // Chip semantics mirror Support_ServiceDesk's toggleType: a group toggles
    // its own type names in and out of one shared set, so two chips compose
    // instead of overriding each other, and an empty set reads as "All".
    toggleTypeChip(group) {
      const next = new Set(this.typeChips);
      if (group.types.every(t => next.has(t))) {
        group.types.forEach(t => next.delete(t));
      } else {
        group.types.forEach(t => next.add(t));
      }
      this.typeChips = [...next];
    },
    isTypeChipActive(group) {
      if (!this.typeChips.length) return false;
      return group.types.every(t => this.typeChips.includes(t));
    },
    clearTypeChips() {
      this.typeChips = [];
    },
    clearAllFilters() {
      this.activeFilters = [];
      this.hideCompleted = false;
      this.typeChips = [];
    },
    isContradictory(chip) {
      if (chip.value === null || chip.scalar) return false;
      const counterOp = chip.operator === 'any_of' ? 'none_of' : 'any_of';
      const counter = this.activeFilters.find(f => f.field === chip.field && f.operator === counterOp);
      return counter?.value?.includes(chip.value) ?? false;
    },
    resolveValueLabel(field, value) {
      if (value === null || value === undefined) return '(any)';
      if (field === 'type') {
        const t = this.requirementTypes.find(t => t.id === value);
        return t ? t.name : String(value);
      }
      if (field === 'owner' || field === 'requester') {
        if (value === '__none__') return field === 'owner' ? 'No owner' : 'No requester';
        const u = this.users.find(u => u.id === value);
        return u ? u.full_name : String(value);
      }
      if (field === 'tenant') {
        return this.tenantFilterOptions.find(o => o.value === value)?.label || String(value);
      }
      if (field === 'tags') return String(value);
      if (field === 'status') {
        const labels = { draft: 'Draft', approved: 'Approved', in_progress: 'In Progress', done: 'Done', open: 'Open', waiting: 'Waiting', resolved: 'Resolved', closed: 'Closed' };
        return labels[value] || value;
      }
      if (field === 'priority') {
        return value ? value.charAt(0).toUpperCase() + value.slice(1) : '';
      }
      if (field === 'source') {
        return value === '__none__' ? 'No source' : SOURCE_LABELS[value] || String(value);
      }
      if (field === 'customer_facing' || field === 'needs_triage' || field === 'owner_is_staff') {
        return value === 'yes' ? 'Yes' : 'No';
      }
      return String(value);
    },
    chipLabel(chip) {
      const fieldLabels = {
        type: 'Type', status: 'Status', owner: 'Owner', requester: 'Requester',
        tenant: 'Tenant', tags: 'Tags', priority: 'Priority',
        customer_facing: 'Customer Facing', source: 'Source',
        needs_triage: 'Needs Triage', owner_is_staff: 'Staff-owned',
        due_date: 'Due', created_at: 'Created', updated_at: 'Updated',
        comment_count: 'Comments', children_count: 'Children',
      };
      const fieldLabel = fieldLabels[chip.field] || chip.field;
      if (chip.scalar) {
        const isCount = COUNT_FILTER_FIELDS.has(chip.field);
        const opLabel = chip.operator === 'on_or_before'
          ? (isCount ? 'at most' : 'on or before')
          : (isCount ? 'at least' : 'on or after');
        return `${fieldLabel} ${opLabel} ${chip.value}`;
      }
      const opLabel = chip.operator === 'none_of' ? 'not' : '';
      const valueLabel = this.resolveValueLabel(chip.field, chip.value);
      return [fieldLabel, opLabel, valueLabel].filter(Boolean).join(' ');
    },
    buildTree(items) {
      const sorted = this.sortItems([...items]);
      const byId = {};
      const roots = [];
      for (const item of sorted) {
        byId[item.id] = { ...item, children: [] };
      }
      for (const item of sorted) {
        const node = byId[item.id];
        if (item.parent && byId[item.parent]) {
          byId[item.parent].children.push(node);
        } else {
          roots.push(node);
        }
      }
      return roots;
    },
    // One comparator for tree and table. Support_Table renders `items` in the
    // order it receives them and never re-sorts, so a column header and the
    // tree agree by construction.
    sortItems(items) {
      const field = this.sortField;
      const dir = this.sortDir;
      const titles = this.parentTitles;
      return items.sort((a, b) => compareWorkItems(a, b, field, dir, titles));
    },
    // asc -> desc -> back to this view mode's default. Only the table has
    // headers, so in practice this always writes the table's slot.
    onSortColumn(key) {
      if (this.sortField !== key) {
        this.sortField = key;
        this.sortDir = 'asc';
      } else if (this.sortDir === 'asc') {
        this.sortDir = 'desc';
      } else {
        this.sortField = this.activeDefaultSort.field;
        this.sortDir = this.activeDefaultSort.dir;
      }
    },
    filterTree(nodes, query) {
      const result = [];
      for (const node of nodes) {
        const rank = matchesSearch(node, query);
        const nodeMatch = rank != null;
        // Only expand all children when the node matched on title or id (rank <= 1).
        // A description/notes match (rank 2) only surfaced this node — it should
        // NOT pull in unrelated children that didn't match anything.
        const titleOrIdMatch = rank != null && rank <= 1;
        const filteredChildren = node.children?.length
          ? this.filterTree(node.children, query)
          : [];
        if (nodeMatch || filteredChildren.length) {
          result.push({ ...node, children: titleOrIdMatch ? node.children : filteredChildren });
        }
      }
      return result;
    },
    async loadRequirementTypes() {
      const response = await axios.get('/api/requirement-types/');
      this.requirementTypes = Array.isArray(response.data) ? response.data : [];
    },
    async loadUsers() {
      const response = await axios.get('/api/user/', { params: { include_staff: 'true' } });
      const data = Array.isArray(response.data) ? response.data : [];
      this.users = data.filter(u => u.email?.endsWith('@kaironhealth.com'));
    },
    async loadRequirements() {
      const isInitialLoad = !Array.isArray(this.allFlat) || this.allFlat.length === 0;
      if (isInitialLoad) {
        const cached = this.loadCachedRequirements();
        if (!cached) this.loading = true;
      } else {
        this.refreshing = true;
      }

      if (isInitialLoad && this.selectedId && this.allFlat.length) {
        this.selectedRequirement = this.allFlat.find(r => r.id === Number(this.selectedId)) || null;
      }

      // The old Requirements tab pinned owner_is_staff=true server-side, which
      // is why staff never saw customer-owned rows there. The merged list has
      // to carry both halves, so the narrowing moves into the Filters panel as
      // an owner_is_staff field the user can set either way or leave off.
      const params = { ...this.filterParams };

      const requestCacheKey = this.cacheKey();
      try {
        const response = await axios.get('/api/requirements/', { params });
        if (!Array.isArray(response.data)) {
          // A malformed 2xx body (e.g. axios's silentJSONParsing fallback on a
          // truncated response) must never poison allFlat — treat it like a
          // failed fetch instead of caching or rendering garbage.
          throw new Error('Malformed /api/requirements/ response: expected an array');
        }
        // The axios tenant interceptor stamps the tenant active at request
        // time, so a response resolving after a tenant/user switch describes
        // the previous context. Drop it rather than paint it over the current
        // list or write it to the current cache key.
        if (requestCacheKey !== this.cacheKey()) return;
        this.allFlat = response.data;
        this.cacheRequirements(response.data);
        this.lastFetchedAt = Date.now();
        this.now = this.lastFetchedAt;
        this.fetchError = false;
        this.retryAttempt = 0;
        clearTimeout(this.retryTimer);
      } catch {
        if (requestCacheKey !== this.cacheKey()) return;
        if (!Array.isArray(this.allFlat)) this.allFlat = [];
        this.fetchError = true;
        this.scheduleRetry();
      } finally {
        // Only the request that still owns the current context settles the
        // spinners — a superseded one leaves them to its replacement.
        if (requestCacheKey === this.cacheKey()) this.finishRequirementsLoad();
      }
    },
    finishRequirementsLoad() {
      // Default tenant filter: all tenants except 3 (Kairon Demo), plus null tenants
      if (!this.tenantDefaultApplied && !this.activeFilters.some(f => f.field === 'tenant') && !this.entityId) {
        const allIds = [...new Set(this.allFlat.map(r => r.tenant).filter(Boolean))];
        const selected = allIds.filter(id => id !== 3);
        if (this.allFlat.some(r => !r.tenant)) selected.unshift('__none__');
        this.activeFilters = [...this.activeFilters, { field: 'tenant', operator: 'any_of', value: selected }];
        this.tenantDefaultApplied = true;
      }

      if (this.selectedId) {
        this.selectedRequirement = this.allFlat.find(r => r.id === Number(this.selectedId)) || null;
      }
      this.loading = false;
      this.refreshing = false;
    },
    scheduleRetry() {
      clearTimeout(this.retryTimer);
      const delay = Math.min(30000, 2000 * 2 ** this.retryAttempt);
      this.retryAttempt++;
      this.retryTimer = setTimeout(this.onRevalidate, delay);
    },
    retryNow() {
      clearTimeout(this.retryTimer);
      this.retryAttempt = 0;
      this.loadRequirements();
    },
    cacheKey() {
      return cacheKeyFor(this.current_tenant_slug, this.current_user?.id);
    },
    requirementsStore() {
      const key = this.cacheKey();
      return key ? new BrowserStore(key, CACHED_REQUIREMENTS_SCHEMA) : null;
    },
    reloadForCacheContext() {
      // A tenant/user switch must not leave the previous context's rows on
      // screen while the replacement request is in flight.
      this.allFlat = [];
      this.selectedRequirement = null;
      // Selection and expansion both hold ids from the outgoing context. A
      // surviving checkedIds is the dangerous one: the bulk bar would keep
      // showing "N selected" and a bulk action would PATCH the previous
      // tenant's requirements while the new tenant is on screen.
      this.checkedIds = new Set();
      this.bulkMode = false;
      this.expandedIds = new Set();
      this.loading = true;
      this.fetchError = false;
      this.loadCachedRequirements();
      this.loadRequirements();
    },
    loadCachedRequirements() {
      // A poisoned entry (e.g. a stringified truncated response) fails schema
      // validation and load() returns the [] default, so it's never treated
      // as a hit — the next successful fetch overwrites it via cacheRequirements.
      const store = this.requirementsStore();
      if (!store) return false;
      const cached = store.load().items;
      if (!cached.length) return false;
      this.allFlat = cached;
      this.loading = false;
      return true;
    },
    cacheRequirements(data) {
      const store = this.requirementsStore();
      if (!store) return;
      evictStaleItemCaches(CACHE_PREFIX, store.key);
      store.save({ items: data });
    },
    onSelect(item) {
      this.selectedRequirement = item;
      this.keyboardFocusedId = item.id;
      this.$emit('select', item.id);
    },
    onToggle(id) {
      if (this.expandedIds.has(id)) {
        this.expandedIds.delete(id);
      } else {
        this.expandedIds.add(id);
      }
      this.expandedIds = new Set(this.expandedIds);
    },
    toggleExpandAll() {
      if (this.allExpanded) {
        this.expandedIds = new Set();
      } else {
        for (const item of this.allFlat) {
          if (item.children_count > 0) {
            this.expandedIds.add(item.id);
          }
        }
        this.expandedIds = new Set(this.expandedIds);
      }
    },
    toggleBulkMode() {
      this.bulkMode = !this.bulkMode;
      if (!this.bulkMode) this.checkedIds = new Set();
    },
    onToggleCheck(id) {
      if (this.checkedIds.has(id)) {
        this.checkedIds.delete(id);
      } else {
        this.checkedIds.add(id);
      }
      this.checkedIds = new Set(this.checkedIds);
    },
    // Only patch rows that are actually loaded. reloadForCacheContext() clears
    // the selection on a tenant/user switch, so a stale id is unreachable
    // today — this keeps a future context-switch path from PATCHing another
    // tenant's requirements. It matters most for _bulkPatchEach, whose
    // callbacks derive the new value from the row: with no row, bulkAddTag's
    // `item?.tags || []` sends the new tag alone and wipes the target's
    // existing tags instead of adding to them.
    loadedSelection(ids) {
      return ids.filter((id) => this.allFlat.some((r) => r.id === id));
    },
    async _bulkPatch(ids, fields) {
      const loaded = this.loadedSelection(ids);
      if (!loaded.length) {
        this.checkedIds = new Set();
        return;
      }
      const failed = [];
      for (const id of loaded) {
        try {
          await axios.patch(`/api/requirements/${id}/`, fields);
        } catch {
          failed.push(id);
        }
      }
      if (failed.length) {
        this.toast.error(`${failed.length} of ${loaded.length} updates failed`);
      }
      this.checkedIds = new Set();
      this.loadRequirements();
    },
    async bulkUpdateStatus(status) {
      if (!status) return;
      await this._bulkPatch([...this.checkedIds], { status });
    },
    async bulkUpdatePriority(priority) {
      if (!priority) return;
      await this._bulkPatch([...this.checkedIds], { priority });
    },
    async bulkReparent(newParentId) {
      await this._bulkPatch([...this.checkedIds], { parent: newParentId });
    },
    async _bulkPatchEach(ids, fn) {
      const loaded = this.loadedSelection(ids);
      if (!loaded.length) {
        this.checkedIds = new Set();
        return;
      }
      const failed = [];
      for (const id of loaded) {
        const item = this.allFlat.find(r => r.id === id);
        try {
          await axios.patch(`/api/requirements/${id}/`, fn(item));
        } catch {
          failed.push(id);
        }
      }
      if (failed.length) {
        this.toast.error(`${failed.length} of ${loaded.length} updates failed`);
      }
      this.checkedIds = new Set();
      this.loadRequirements();
    },
    async bulkAddTag(tag) {
      const trimmed = tag.trim();
      if (!trimmed) return;
      await this._bulkPatchEach([...this.checkedIds], (item) => {
        const current = item?.tags || [];
        if (current.includes(trimmed)) return { tags: current };
        return { tags: [...current, trimmed] };
      });
    },
    async bulkRemoveTag(tag) {
      await this._bulkPatchEach([...this.checkedIds], (item) => {
        const current = item?.tags || [];
        return { tags: current.filter(t => t !== tag) };
      });
    },
    async bulkSetDueDate(date) {
      const value = date || null;
      await this._bulkPatch([...this.checkedIds], { due_date: value });
    },
    onUpdateField(payload) {
      const { id, ...fields } = payload;
      const item = this.allFlat.find((r) => r.id === id);
      const oldFields = {};
      if (item) {
        for (const key of Object.keys(fields)) {
          oldFields[key] = item[key];
          item[key] = fields[key];
        }
      }
      this.cacheRequirements(this.allFlat);
      const fieldName = Object.keys(fields)[0];
      const labels = { status: 'Status', priority: 'Priority', owner: 'Owner', tenant: 'Tenant' };
      const label = labels[fieldName] || fieldName;
      this.$refs.undoToast.show(
        `${label} updated`,
        async () => {
          await axios.patch(`/api/requirements/${id}/`, fields);
          await this.loadRequirements();
        },
        () => {
          if (item) { for (const key of Object.keys(oldFields)) item[key] = oldFields[key]; }
          this.cacheRequirements(this.allFlat);
        },
      );
    },
    onUpdateTitle(payload) {
      const item = this.allFlat.find((r) => r.id === payload.id);
      const oldTitle = item?.title;
      if (item) item.title = payload.title;
      this.cacheRequirements(this.allFlat);
      this.$refs.undoToast.show(
        'Title updated',
        async () => {
          await axios.patch(`/api/requirements/${payload.id}/`, { title: payload.title });
          await this.loadRequirements();
        },
        () => { if (item) item.title = oldTitle; this.cacheRequirements(this.allFlat); },
      );
    },
    onQuickAdd(parentNode) {
      // Expand parent if collapsed
      if (!this.expandedIds.has(parentNode.id)) {
        this.expandedIds.add(parentNode.id);
        this.expandedIds = new Set(this.expandedIds);
      }
      this.quickAddParent = parentNode;
    },
    onRootDragStart() {
      document.body.classList.add('is-dragging-requirement');
    },
    onRootDragEnd(evt) {
      document.body.classList.remove('is-dragging-requirement');
      const fromParentId = evt.from.dataset.parentId;
      const toParentId = evt.to.dataset.parentId;
      const item = this.filteredTree[evt.newIndex];

      if (fromParentId !== toParentId) {
        this.onReparent({
          id: item?.id,
          newParentId: toParentId ? Number(toParentId) : null,
          newIndex: evt.newIndex,
        });
      } else {
        const items = this.filteredTree.map((node, index) => ({
          id: node.id,
          position: index,
        }));
        this.onReorder(items);
      }
    },
    onReorder(items) {
      // Update positions in allFlat so computed tree reflects new order
      const posMap = {};
      for (const item of items) posMap[item.id] = item.position;
      this.allFlat = this.allFlat.map(r =>
        posMap[r.id] !== undefined ? { ...r, position: posMap[r.id] } : r
      );
      this.cacheRequirements(this.allFlat);
      axios.post('/api/requirements/reorder/', { items });
    },
    async onReparent({ id, newParentId, newIndex }) {
      await axios.patch(`/api/requirements/${id}/`, {
        parent: newParentId || null,
        position: newIndex,
      });
      this.loadRequirements();
    },
    async onQuickAddCreated(data) {
      await axios.post('/api/requirements/', data);
      this.quickAddParent = null;
      this.loadRequirements();
    },
    closeDetail() {
      this.selectedRequirement = null;
      this.$emit('select', null);
    },
    onRequirementCreated() {
      this.showCreateModal = false;
      this.loadRequirements();
    },
    onRequirementUpdated(updated) {
      this.loadRequirements();
      this.selectedRequirement = updated;
    },
    onRequirementDeleted() {
      this.selectedRequirement = null;
      this.$emit('select', null);
      this.loadRequirements();
    },
    onSelectItem(item) {
      this.selectedRequirement = item;
      this.$emit('select', item.id);
    },
    openCreateWithParent(parentItem) {
      this.createModalParentId = parentItem.id;
      this.showCreateModal = true;
    },
    onActionsMenu({ event, node }) {
      const rect = event.currentTarget.getBoundingClientRect();
      this.contextMenu = { visible: true, x: rect.right, y: rect.bottom + 4, item: node };
    },
    onContextChangeParent(item) {
      this.selectedRequirement = item;
      this.$emit('select', item.id);
    },
    async onContextCopyLink(item) {
      const url = `${window.location.origin}${window.location.pathname}?selected=${item.id}`;
      try {
        await navigator.clipboard.writeText(url);
        this.toast.success('Link copied to clipboard.');
      } catch {
        this.toast.error('Failed to copy link.');
      }
    },
    async onContextDelete(item) {
      try {
        await axios.delete(`/api/requirements/${item.id}/`);
        this.toast.success('Deleted successfully.');
        if (this.selectedRequirement?.id === item.id) {
          this.selectedRequirement = null;
          this.$emit('select', null);
        }
        this.loadRequirements();
      } catch {
        this.toast.error('Failed to delete. Please try again.');
      }
    },
    async onContextDeletePromote(item) {
      const children = this.allFlat.filter((r) => r.parent === item.id);
      const failed = [];
      for (const child of children) {
        try {
          await axios.patch(`/api/requirements/${child.id}/`, {
            parent: item.parent || null,
          });
        } catch {
          failed.push(child.title);
        }
      }
      if (failed.length) {
        this.toast.error(
          `Could not move ${failed.length} ${failed.length === 1 ? 'child' : 'children'}. Delete aborted.`,
        );
        this.loadRequirements();
        return;
      }
      try {
        await axios.delete(`/api/requirements/${item.id}/`);
        this.toast.success('Deleted. Children moved to parent.');
        if (this.selectedRequirement?.id === item.id) {
          this.selectedRequirement = null;
          this.$emit('select', null);
        }
        this.loadRequirements();
      } catch {
        this.toast.error('Children were moved but delete failed. Please try again.');
      }
    },
    onCommentCount({ id, count }) {
      this.commentCounts = { ...this.commentCounts, [id]: count };
    },
    exportCsv() {
      const formatStatus = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
      const rows = [['Type', 'Title', 'Status', 'Priority', 'Owner', 'Requester', 'Due Date']];
      const items = this.viewMode === 'table' ? this.tableData : this.tagFiltered;
      for (const item of items) {
        rows.push([
          item.requirement_type_name || '',
          `"${(item.title || '').replace(/"/g, '""')}"`,
          formatStatus(item.status),
          (item.priority || '').toUpperCase(),
          item.owner_name || '',
          item.requester_name || '',
          item.due_date || '',
        ]);
      }
      const csv = rows.map((r) => r.join(',')).join('\n');
      const blob = new Blob([csv], { type: 'text/csv' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `support-work-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    },
    // Keyboard navigation
    onKeydown(e) {
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;
      if (this.showCreateModal) return;
      const ids = this.flatVisibleIds;
      if (!ids.length) return;
      const currentIndex = ids.indexOf(this.keyboardFocusedId);

      if (e.key === 'ArrowDown' || e.key === 'j') {
        e.preventDefault();
        const next = currentIndex < ids.length - 1 ? currentIndex + 1 : 0;
        this.keyboardFocusedId = ids[next];
      } else if (e.key === 'ArrowUp' || e.key === 'k') {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : ids.length - 1;
        this.keyboardFocusedId = ids[prev];
      } else if (e.key === 'Enter') {
        e.preventDefault();
        if (this.keyboardFocusedId) {
          const item = this.allFlat.find(r => r.id === this.keyboardFocusedId);
          if (item) this.onSelect(item);
        }
      } else if (e.key === 'Escape') {
        e.preventDefault();
        if (this.bulkDropdown) {
          this.bulkDropdown = null;
        } else if (this.filterPopoverOpen) {
          this.filterPopoverOpen = false;
        } else if (this.detailOpen) {
          this.closeDetail();
        } else {
          this.keyboardFocusedId = null;
        }
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        if (this.keyboardFocusedId && !this.expandedIds.has(this.keyboardFocusedId)) {
          this.onToggle(this.keyboardFocusedId);
        }
      } else if (e.key === 'ArrowLeft') {
        e.preventDefault();
        if (this.keyboardFocusedId && this.expandedIds.has(this.keyboardFocusedId)) {
          this.onToggle(this.keyboardFocusedId);
        }
      }
    },
  },
};
</script>
